"use client";

import {
  Container,
  Title,
  Text,
  Box,
  SimpleGrid,
  Paper,
  Stack,
  Button,
  Select,
  Group,
  Anchor,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { seasons, getComparisonsByParent } from "./comparisonData";
import { useTranslations } from "next-intl";

export default function ComparisonHubContent() {
  const t = useTranslations("colorPaletteComparison");
  const router = useRouter();
  const [seasonA, setSeasonA] = useState<string | null>(null);
  const [seasonB, setSeasonB] = useState<string | null>(null);

  const comparisonsByParent = getComparisonsByParent();

  const handleCompare = () => {
    if (!seasonA || !seasonB || seasonA === seasonB) return;

    // Sort alphabetically for canonical URL
    const [first, second] = [seasonA, seasonB].sort((a, b) => a.localeCompare(b));
    router.push(`/color-palette/comparison/${first}-vs-${second}`);
  };

  const seasonOptions = seasons.map(s => ({
    value: s.id,
    label: s.name,
  }));

  const parentSeasonLabels: Record<string, string> = {
    spring: "Spring",
    summer: "Summer",
    autumn: "Autumn",
    winter: "Winter",
  };

  const parentSeasonColors: Record<string, string> = {
    spring: "#FFB347",
    summer: "#87CEEB",
    autumn: "#CD853F",
    winter: "#4169E1",
  };

  return (
    <Box py={{ base: "xl", md: "3rem" }}>
      <Container size="lg">
        {/* Hero */}
        <Stack gap="md" ta="center" mb="xl">
          <Title order={1} fz={{ base: "1.75rem", md: "2.5rem" }} style={{ color: "#1E293B" }}>
            {t("hub.title")}
          </Title>
          <Text fz={{ base: "md", md: "lg" }} c="dimmed" maw={600} mx="auto">
            {t("hub.description")}
          </Text>
        </Stack>

        {/* Season Selectors */}
        <Paper
          p={{ base: "md", md: "xl" }}
          radius="lg"
          mb="xl"
          style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
            border: "1px solid #E2E8F0",
          }}
        >
          <Stack gap="md">
            <Text fw={600} ta="center" fz="lg" style={{ color: "#1E293B" }}>
              {t("hub.selectTitle")}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <Select
                placeholder={t("hub.selectFirst")}
                data={seasonOptions}
                value={seasonA}
                onChange={setSeasonA}
                size="md"
                radius="md"
                searchable
              />
              <Select
                placeholder={t("hub.selectSecond")}
                data={seasonOptions}
                value={seasonB}
                onChange={setSeasonB}
                size="md"
                radius="md"
                searchable
              />
              <Button
                size="md"
                radius="md"
                rightSection={<IconArrowRight size={18} />}
                onClick={handleCompare}
                disabled={!seasonA || !seasonB || seasonA === seasonB}
                color="blue"
              >
                {t("hub.compareButton")}
              </Button>
            </SimpleGrid>
            {seasonA && seasonB && seasonA === seasonB && (
              <Text c="red" size="sm" ta="center">
                {t("hub.sameSeasonError")}
              </Text>
            )}
          </Stack>
        </Paper>

        {/* Explore Comparisons Grid */}
        <Box mb="xl">
          <Title order={2} fz={{ base: "1.25rem", md: "1.75rem" }} mb="lg" style={{ color: "#1E293B" }}>
            {t("hub.exploreTitle")}
          </Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {(["spring", "summer", "autumn", "winter"] as const).map(parent => (
              <Paper
                key={parent}
                p="lg"
                radius="lg"
                style={{
                  border: "1px solid #E2E8F0",
                  borderTop: `4px solid ${parentSeasonColors[parent]}`,
                }}
              >
                <Title order={3} fz="lg" mb="md" style={{ color: "#1E293B" }}>
                  {parentSeasonLabels[parent]} Comparisons
                </Title>
                <Stack gap="xs">
                  {comparisonsByParent[parent].slice(0, 12).map(comparison => (
                    <Anchor
                      key={comparison.slug}
                      href={`/color-palette/comparison/${comparison.slug}`}
                      fz="sm"
                      style={{ color: "#3B82F6" }}
                    >
                      {comparison.name}
                    </Anchor>
                  ))}
                  {comparisonsByParent[parent].length > 12 && (
                    <Text fz="xs" c="dimmed">
                      {t("hub.moreComparisons", { count: comparisonsByParent[parent].length - 12 })}
                    </Text>
                  )}
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        {/* Popular Comparisons */}
        <Box mb="xl">
          <Title order={2} fz={{ base: "1.25rem", md: "1.75rem" }} mb="lg" style={{ color: "#1E293B" }}>
            {t("hub.popularTitle")}
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {[
              { slug: "light-spring-vs-light-summer", name: "Light Spring vs Light Summer" },
              { slug: "soft-autumn-vs-soft-summer", name: "Soft Autumn vs Soft Summer" },
              { slug: "bright-spring-vs-true-winter", name: "Bright Spring vs True Winter" },
              { slug: "deep-autumn-vs-deep-winter", name: "Deep Autumn vs Deep Winter" },
              { slug: "cool-winter-vs-true-summer", name: "Cool Winter vs True Summer" },
              { slug: "soft-autumn-vs-true-autumn", name: "Soft Autumn vs True Autumn" },
            ].map(item => (
              <Paper
                key={item.slug}
                component="a"
                href={`/color-palette/comparison/${item.slug}`}
                p="md"
                radius="md"
                style={{
                  border: "1px solid #E2E8F0",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#3B82F6";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#E2E8F0";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <Group justify="space-between" align="center">
                  <Text fw={500} style={{ color: "#1E293B" }}>
                    {item.name}
                  </Text>
                  <IconArrowRight size={16} color="#94A3B8" />
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        {/* SEO Content */}
        <Box>
          <Title order={2} fz={{ base: "1.25rem", md: "1.5rem" }} mb="md" style={{ color: "#1E293B" }}>
            {t("hub.understandingTitle")}
          </Title>
          <Stack gap="md">
            <Text c="dimmed">
              {t("hub.understandingIntro")}
            </Text>
            <Box>
              <Text fw={600} style={{ color: "#1E293B" }}>{t("hub.temperatureTitle")}</Text>
              <Text c="dimmed" size="sm">
                {t("hub.temperatureDesc")}
              </Text>
            </Box>
            <Box>
              <Text fw={600} style={{ color: "#1E293B" }}>{t("hub.valueTitle")}</Text>
              <Text c="dimmed" size="sm">
                {t("hub.valueDesc")}
              </Text>
            </Box>
            <Box>
              <Text fw={600} style={{ color: "#1E293B" }}>{t("hub.chromaTitle")}</Text>
              <Text c="dimmed" size="sm">
                {t("hub.chromaDesc")}
              </Text>
            </Box>
            <Text c="dimmed">
              {t("hub.understandingConclusion")}
            </Text>
          </Stack>
        </Box>

        {/* CTA */}
        <Paper
          mt="xl"
          p="xl"
          radius="lg"
          ta="center"
          style={{
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
          }}
        >
          <Title order={3} c="white" mb="sm">
            {t("hub.ctaTitle")}
          </Title>
          <Text c="white" mb="md" style={{ opacity: 0.9 }}>
            {t("hub.ctaDescription")}
          </Text>
          <Button
            component="a"
            href="/ai-color-analysis"
            size="lg"
            radius="md"
            variant="white"
            color="blue"
            rightSection={<IconArrowRight size={18} />}
          >
            {t("hub.ctaButton")}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
